import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import privacyHero from "@/assets/privacy-hero.jpg";

const Privacy = () => {
  return (
    <Layout>
      <SEO 
        title="Privacy Policy - Random Number Generator"
        description="Read our privacy policy to understand how we protect your data when using our random number generator and related tools."
        canonical="https://numbers-generator.com/privacy"
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen Numbers Generator Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </header>

        <div className="space-y-6">
          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Your Privacy Matters</h2>
            <p className="text-muted-foreground leading-relaxed">
              At RandomGen, we take your privacy seriously. This policy outlines how we collect, use, and protect 
              your information when you use our random number generation services.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Location Data (Optional)</h3>
                <p className="leading-relaxed mb-3">
                  With your explicit consent, we may collect:
                </p>
                <ul className="space-y-2 ml-6">
                  <li>• <strong>GPS coordinates</strong>: Latitude and longitude (accurate to ~10 meters)</li>
                  <li>• <strong>Street address</strong>: Reverse-geocoded from GPS coordinates</li>
                  <li>• <strong>City, region, and country</strong>: From GPS or IP-based lookup</li>
                  <li>• <strong>IP address</strong>: Used for approximate location and security</li>
                </ul>
                <p className="leading-relaxed mt-3 text-sm">
                  <strong>Purpose:</strong> Location data helps us provide localized experiences, detect fraud, 
                  and understand geographic usage patterns.
                  <br />
                  <strong>Retention:</strong> Location data is automatically deleted after 90 days.
                  <br />
                  <strong>Consent:</strong> You will be asked for permission before any location data is collected. 
                  You can manage your preferences in{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline">Privacy Settings</a>.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Anonymous Usage Data</h3>
                <p className="leading-relaxed">
                  We collect minimal anonymous data including basic usage statistics, browser type, device type, 
                  and page views to improve our services. This data cannot identify you personally.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Registered Users</h3>
                <p className="leading-relaxed">
                  If you create an account, we collect your email address and password (securely hashed). 
                  We store usage statistics to provide personalized insights. 
                  <strong> We never collect the actual random numbers you generate.</strong>
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>To provide and improve our random number generation services</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>To analyze usage patterns and optimize performance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>To send important service updates (for registered users only)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>To prevent abuse and ensure fair usage of our services</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We implement industry-standard security measures to protect your data. All communications are encrypted 
              using HTTPS, and user passwords are securely hashed using modern cryptographic algorithms.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              The random numbers you generate are processed client-side in your browser and are not transmitted to 
              our servers. Only usage metadata is collected for statistical purposes.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Cookies and Tracking</h2>
            <div className="space-y-3 text-muted-foreground">
              <p className="leading-relaxed">
                We use essential cookies to maintain your session and provide a seamless experience. 
              </p>
              <div className="space-y-2">
                <p className="font-semibold text-foreground">Cookie Types:</p>
                <ul className="ml-6 space-y-1">
                  <li>• <strong>Essential cookies</strong>: Required for authentication and session management</li>
                  <li>• <strong>Preference cookies</strong>: Remember your privacy settings and preferences</li>
                  <li>• <strong>Analytics cookies</strong>: Help us understand how you use our service (can be disabled)</li>
                </ul>
              </div>
              <p className="leading-relaxed text-sm mt-3">
                We do not use third-party advertising cookies or sell your data to advertisers. 
                Location data is cached locally in your browser for 5 minutes for performance.
              </p>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              RandomGen may use trusted third-party services for authentication and analytics. These services operate 
              under their own privacy policies, and we ensure they meet our standards for data protection.
            </p>
          </Card>

          <Card className="p-8 border-primary/20">
            <h2 className="text-2xl font-semibold mb-4">Your Privacy Rights (GDPR/CCPA)</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Under GDPR (European Union) and CCPA (California), you have comprehensive rights regarding your personal data:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Access</h3>
                <p className="text-sm text-muted-foreground">
                  You can request a copy of all personal data we hold about you. 
                  Use our{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline">Privacy Settings</a>
                  {' '}page to export your data instantly.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Rectification</h3>
                <p className="text-sm text-muted-foreground">
                  You can request correction of inaccurate or incomplete personal data.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Erasure ("Right to be Forgotten")</h3>
                <p className="text-sm text-muted-foreground">
                  You can request complete deletion of your account and all associated data. 
                  We'll process your request within 30 days. Visit{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline">Privacy Settings</a>
                  {' '}or{' '}
                  <a href="/contact" className="text-primary hover:underline">contact us</a>.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Object & Opt-Out</h3>
                <p className="text-sm text-muted-foreground">
                  You can opt-out of location tracking and analytics at any time. 
                  Manage preferences in{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline">Privacy Settings</a>.
                  Opting out won't affect core functionality.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Data Portability</h3>
                <p className="text-sm text-muted-foreground">
                  Export your data in machine-readable JSON format from{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline">Privacy Settings</a>.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Right to Restrict Processing</h3>
                <p className="text-sm text-muted-foreground">
                  You can request that we limit how we process your data.
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-primary/5 rounded-lg">
              <p className="text-sm font-semibold mb-2">Exercise Your Rights</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  • Visit{' '}
                  <a href="/privacy-settings" className="text-primary hover:underline font-medium">
                    Privacy Settings
                  </a>
                  {' '}to manage preferences and export data
                </p>
                <p>
                  • Contact us via our{' '}
                  <a href="/contact" className="text-primary hover:underline font-medium">
                    Contact Page
                  </a>
                  {' '}for data deletion or other requests
                </p>
                <p>• We'll respond to all requests within 30 days as required by law</p>
              </div>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our services are not directed at children under 13 years of age. We do not knowingly collect personal 
              information from children. If you believe we have inadvertently collected such information, please 
              contact us immediately.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this privacy policy from time to time. We will notify users of significant changes by 
              posting a notice on our website or sending an email to registered users.
            </p>
          </Card>

          <Card className="p-8 bg-accent/50">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this privacy policy or your data, please visit our{' '}
              <a href="/contact" className="text-primary hover:underline">contact page</a>.
            </p>
          </Card>
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={privacyHero} 
            alt="Data privacy and security concept showing encrypted documents, secure locks, GDPR compliance, and data protection for random number generator users"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
